import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { apiFetch } from "../api/api";

const fmtNum = (v, digits = 2) => {
  if (v === null || v === undefined || v === "") return "-";
  const n = Number(v);
  if (!Number.isFinite(n)) return String(v);
  return n.toLocaleString("en-IN", {
    minimumFractionDigits: 0,
    maximumFractionDigits: digits,
  });
};

const fmtDate = (v) => (v ? String(v) : "-");
const fmtSignedPct = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "-";
  return `${n >= 0 ? "+" : ""}${n.toFixed(2)}%`;
};
const fmtPctTick = (v) => {
  const n = Number(v);
  if (!Number.isFinite(n)) return "";
  return `${n.toFixed(1)}%`;
};

const getPremiumInfo = (ltpValue, navValue) => {
  const ltp = Number(ltpValue);
  const nav = Number(navValue);
  if (!Number.isFinite(ltp) || !Number.isFinite(nav) || nav === 0) return null;

  const premiumPct = ((ltp - nav) / nav) * 100;
  const absPct = Math.abs(premiumPct);

  let label = "HIGH";
  let tone = "error";

  if (premiumPct >= -0.5 && premiumPct <= 0.5) {
    label = "Fair Value";
    tone = "success";
  } else if (absPct <= 1.5) {
    label = "Mild";
    tone = "info";
  } else if (absPct <= 3) {
    label = "Moderate";
    tone = "warning";
  }

  return { ltp, nav, premiumPct, label, tone };
};

const HISTORY_RANGE_OPTIONS = [
  { value: "3M", label: "3M" },
  { value: "6M", label: "6M" },
  { value: "1Y", label: "1Y" },
  { value: "3Y", label: "3Y" },
  { value: "ALL", label: "All" },
];

const parseYmdLocal = (v) => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(v || ""));
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
};

const inSelectedRange = (tradeDate, endDate, range) => {
  if (range === "ALL") return true;
  const d = parseYmdLocal(tradeDate);
  if (!d || !endDate) return false;

  const cutoff = new Date(endDate);
  if (range.endsWith("M")) {
    cutoff.setMonth(cutoff.getMonth() - Number(range.replace("M", "")));
  } else if (range.endsWith("Y")) {
    cutoff.setFullYear(cutoff.getFullYear() - Number(range.replace("Y", "")));
  }
  return d >= cutoff;
};

const detailFields = [
  { key: "symbol", label: "Symbol" },
  { key: "etf_name", label: "ETF Name" },
  { key: "amcname", label: "AMC" },
  { key: "biname", label: "Benchmark" },
  { key: "trade_date", label: "Trade Date" },
  { key: "underlying_asset", label: "Underlying Asset" },
  { key: "open_price", label: "Open" },
  { key: "high_price", label: "High" },
  { key: "low_price", label: "Low" },
  { key: "prev_close", label: "Prev Close" },
  { key: "ltp", label: "LTP" },
  { key: "indicative_close", label: "Indicative Close" },
  { key: "change_value", label: "Change" },
  { key: "change_percent", label: "% Change" },
  { key: "volume", label: "Volume" },
  { key: "value_crores", label: "Value (Crores)" },
  { key: "nav", label: "NAV" },
  { key: "week52_high", label: "52W High" },
  { key: "week52_low", label: "52W Low" },
  { key: "chng_30d_percent", label: "30D % Change" },
  { key: "chng_365d_percent", label: "365D % Change" },
  { key: "source_file_name", label: "Source File" },
];

export default function EtfDailyDataViewer({
  title = "ETF Daily Data",
  subtitle = "View imported ETF daily market data by symbol and date.",
  showUpload = false,
}) {
  const [filters, setFilters] = useState({ date: "", q: "" });
  const [rows, setRows] = useState([]);
  const [listMeta, setListMeta] = useState({
    requested_date: null,
    effective_date: null,
    used_fallback_date: false,
  });
  const [loadingList, setLoadingList] = useState(false);
  const [listError, setListError] = useState("");

  const [selectedSymbol, setSelectedSymbol] = useState("");
  const [detailDate, setDetailDate] = useState("");
  const [detailData, setDetailData] = useState(null);
  const [availableDates, setAvailableDates] = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState("");
  const [historyRows, setHistoryRows] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyError, setHistoryError] = useState("");
  const [historyRange, setHistoryRange] = useState("1Y");

  const [uploadDate, setUploadDate] = useState("");
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState("");
  const [uploadSummary, setUploadSummary] = useState(null);

  const [holdingsDate, setHoldingsDate] = useState("");
  const [holdingsFile, setHoldingsFile] = useState(null);
  const [uploadingHoldings, setUploadingHoldings] = useState(false);
  const [holdingsUploadError, setHoldingsUploadError] = useState("");
  const [holdingsUploadSummary, setHoldingsUploadSummary] = useState(null);

  const [imports, setImports] = useState([]);
  const [holdingsImports, setHoldingsImports] = useState([]);
  const [loadingImports, setLoadingImports] = useState(false);
  const [loadingHoldingsImports, setLoadingHoldingsImports] = useState(false);
  const [refreshNo, setRefreshNo] = useState(0);

  const columns = useMemo(
    () => [
      { field: "symbol", headerName: "Symbol", width: 130 },
      { field: "etf_name", headerName: "ETF", flex: 1, minWidth: 260 },
      { field: "amcname", headerName: "AMC", minWidth: 180, flex: 0.7 },
      {
        field: "ltp",
        headerName: "LTP",
        width: 110,
        renderCell: (params) => fmtNum(params.row?.ltp),
      },
      {
        field: "nav",
        headerName: "NAV",
        width: 110,
        renderCell: (params) => fmtNum(params.row?.nav),
      },
      {
        field: "premium_discount_pct",
        headerName: "Prem/Disc %",
        width: 140,
        renderCell: (params) => {
          const info = getPremiumInfo(params.row?.ltp, params.row?.nav);
          return info ? fmtSignedPct(info.premiumPct) : "-";
        },
      },
      {
        field: "change_percent",
        headerName: "%Chng",
        width: 110,
        renderCell: (params) => fmtNum(params.row?.change_percent),
      },
      {
        field: "volume",
        headerName: "Volume",
        width: 140,
        renderCell: (params) => fmtNum(params.row?.volume, 0),
      },
      {
        field: "value_crores",
        headerName: "Value Cr",
        width: 130,
        renderCell: (params) => fmtNum(params.row?.value_crores),
      },
      { field: "trade_date", headerName: "Date", width: 120 },
    ],
    []
  );

  const fetchList = async () => {
    setLoadingList(true);
    setListError("");
    try {
      const qs = new URLSearchParams();
      if (filters.date) qs.set("date", filters.date);
      if (filters.q.trim()) qs.set("q", filters.q.trim());
      const url = `/api/v1/auth/etf/daily/symbols${qs.toString() ? `?${qs.toString()}` : ""}`;
      const data = await apiFetch(url);
      const nextRows = (data.rows || []).map((r) => ({
        ...r,
        id: `${r.trade_date || "date"}-${r.symbol}`,
      }));
      setRows(nextRows);
      setListMeta({
        requested_date: data.requested_date || null,
        effective_date: data.effective_date || null,
        used_fallback_date: Boolean(data.used_fallback_date),
      });

      setSelectedSymbol((prev) => {
        if (prev && nextRows.some((r) => r.symbol === prev)) return prev;
        return nextRows[0]?.symbol || "";
      });
    } catch (err) {
      setRows([]);
      setListError(err.message || "Failed to load imported data");
    } finally {
      setLoadingList(false);
    }
  };

  const fetchDetail = async (symbol, forcedDate = detailDate) => {
    if (!symbol) {
      setDetailData(null);
      setAvailableDates([]);
      return;
    }

    setLoadingDetail(true);
    setDetailError("");
    try {
      const qs = new URLSearchParams();
      if (forcedDate) qs.set("date", forcedDate);
      const data = await apiFetch(
        `/api/v1/auth/etf/daily/symbol/${encodeURIComponent(symbol)}${
          qs.toString() ? `?${qs.toString()}` : ""
        }`
      );

      setDetailData(data.row || null);
      setAvailableDates(data.available_dates || []);
    } catch (err) {
      setDetailData(null);
      setDetailError(err.message || "Failed to load symbol details");
      setAvailableDates([]);
    } finally {
      setLoadingDetail(false);
    }
  };

  const fetchImports = async () => {
    if (!showUpload) return;
    setLoadingImports(true);
    try {
      const data = await apiFetch("/api/v1/auth/admin/etf/daily/imports?limit=8");
      const sortedRows = [...(data.rows || [])].sort((a, b) => {
        const dateCompare = String(b.trade_date || "").localeCompare(String(a.trade_date || ""));
        if (dateCompare !== 0) return dateCompare;
        return String(b.created_at || "").localeCompare(String(a.created_at || ""));
      });
      setImports(sortedRows);
    } catch (_) {
      setImports([]);
    } finally {
      setLoadingImports(false);
    }
  };

  const fetchHoldingsImports = async () => {
    if (!showUpload) return;
    setLoadingHoldingsImports(true);
    try {
      const data = await apiFetch("/api/v1/auth/admin/etf/holders/imports?limit=8");
      setHoldingsImports(data.rows || []);
    } catch (_) {
      setHoldingsImports([]);
    } finally {
      setLoadingHoldingsImports(false);
    }
  };

  const fetchHistory = async (symbol) => {
    if (!symbol) {
      setHistoryRows([]);
      setHistoryError("");
      return;
    }

    setLoadingHistory(true);
    setHistoryError("");
    try {
      const data = await apiFetch(
        `/api/v1/auth/etf/daily/symbol/${encodeURIComponent(symbol)}/history?limit=2000`
      );
      const rows = (data.rows || []).map((r) => {
        const ltp = Number(r.ltp);
        const nav = Number(r.nav);
        const premiumDiscountPct =
          Number.isFinite(ltp) && Number.isFinite(nav) && nav !== 0
            ? ((ltp - nav) / nav) * 100
            : null;
        return {
          trade_date: r.trade_date,
          ltp: Number.isFinite(ltp) ? ltp : null,
          nav: Number.isFinite(nav) ? nav : null,
          premium_discount_pct: premiumDiscountPct,
        };
      });
      setHistoryRows(rows);
    } catch (err) {
      setHistoryRows([]);
      setHistoryError(err.message || "Failed to load chart history");
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => {
    fetchList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.date, filters.q, refreshNo]);

  useEffect(() => {
    fetchDetail(selectedSymbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol, detailDate]);

  useEffect(() => {
    fetchHistory(selectedSymbol);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedSymbol, refreshNo]);

  useEffect(() => {
    fetchImports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNo, showUpload]);

  useEffect(() => {
    fetchHoldingsImports();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshNo, showUpload]);

  const onUpload = async () => {
    if (!uploadFile) {
      setUploadError("Please select a CSV file");
      return;
    }

    setUploading(true);
    setUploadError("");
    setUploadSummary(null);
    try {
      const form = new FormData();
      form.append("file", uploadFile);
      if (uploadDate) form.append("trade_date", uploadDate);

      const data = await apiFetch("/api/v1/auth/admin/etf/daily/upload", {
        method: "POST",
        body: form,
      });

      const summary = data.summary || null;
      setUploadSummary(summary);
      if (summary?.trade_date) {
        setFilters((p) => ({ ...p, date: summary.trade_date }));
        setDetailDate(summary.trade_date);
      }

      setUploadFile(null);
      setRefreshNo((x) => x + 1);
    } catch (err) {
      setUploadError(err.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const onUploadHoldings = async () => {
    if (!holdingsDate) {
      setHoldingsUploadError("Please select snapshot date");
      return;
    }
    if (!holdingsFile) {
      setHoldingsUploadError("Please select holdings Excel");
      return;
    }

    setUploadingHoldings(true);
    setHoldingsUploadError("");
    setHoldingsUploadSummary(null);
    try {
      const form = new FormData();
      form.append("file", holdingsFile);
      form.append("snapshot_date", holdingsDate);

      const data = await apiFetch("/api/v1/auth/admin/etf/holders/upload", {
        method: "POST",
        body: form,
      });

      setHoldingsUploadSummary(data.summary || null);
      setHoldingsFile(null);
      setRefreshNo((x) => x + 1);
    } catch (err) {
      setHoldingsUploadError(err.message || "Holdings upload failed");
    } finally {
      setUploadingHoldings(false);
    }
  };

  const renderDetailValue = (key, val) => {
    if (
      [
        "open_price",
        "high_price",
        "low_price",
        "prev_close",
        "ltp",
        "indicative_close",
        "change_value",
        "change_percent",
        "value_crores",
        "nav",
        "week52_high",
        "week52_low",
        "chng_30d_percent",
        "chng_365d_percent",
      ].includes(key)
    ) {
      return fmtNum(val);
    }
    if (key === "volume") return fmtNum(val, 0);
    if (key === "trade_date") return fmtDate(val);
    return val || "-";
  };

  const premiumInfo = useMemo(
    () => getPremiumInfo(detailData?.ltp, detailData?.nav),
    [detailData?.ltp, detailData?.nav]
  );

  const filteredHistoryRows = useMemo(() => {
    if (!historyRows.length) return [];
    if (historyRange === "ALL") return historyRows;

    const endDate = parseYmdLocal(historyRows[historyRows.length - 1]?.trade_date);
    if (!endDate) return historyRows;

    return historyRows.filter((r) => inSelectedRange(r.trade_date, endDate, historyRange));
  }, [historyRows, historyRange]);

  return (
    <Stack spacing={2}>
      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack spacing={0.5}>
          <Typography variant="h5" sx={{ fontWeight: 800 }}>
            {title}
          </Typography>
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {subtitle}
          </Typography>
        </Stack>
      </Paper>

      {showUpload ? (
        <Stack
          direction={{ xs: "column", lg: "row" }}
          spacing={2}
          alignItems="stretch"
          sx={{ width: "100%" }}
        >
          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, flex: 1 }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Upload Daily CSV
              </Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
                <TextField
                  size="small"
                  type="date"
                  label="Trade date (optional)"
                  InputLabelProps={{ shrink: true }}
                  value={uploadDate}
                  onChange={(e) => setUploadDate(e.target.value)}
                  sx={{ maxWidth: 220 }}
                />
                <Button variant="outlined" component="label">
                  {uploadFile ? uploadFile.name : "Select CSV"}
                  <input
                    hidden
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
                  />
                </Button>
                <Button variant="contained" onClick={onUpload} disabled={uploading}>
                  {uploading ? "Uploading..." : "Upload & Import"}
                </Button>
              </Stack>

              {uploadError ? <Alert severity="error">{uploadError}</Alert> : null}
              {uploadSummary ? (
                <Alert severity="success">
                  Imported for {uploadSummary.trade_date}: {uploadSummary.processed_rows} processed (
                  {uploadSummary.inserted_rows} inserted, {uploadSummary.updated_rows} updated,{" "}
                  {uploadSummary.skipped_rows} skipped)
                </Alert>
              ) : null}

              <Divider />

              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Recent Daily Imports
              </Typography>

              {loadingImports ? (
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Loading import history...
                </Typography>
              ) : imports.length ? (
                <Stack spacing={0.6}>
                  {imports.map((r) => (
                    <Stack
                      key={r.import_batch_id}
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      sx={{ p: 1, borderRadius: 1.5, background: "rgba(148,163,184,0.08)" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {r.trade_date} - {r.source_file_name || "file"}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        P:{r.processed_rows} I:{r.inserted_rows} U:{r.updated_rows} S:{r.skipped_rows}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  No imports yet.
                </Typography>
              )}
            </Stack>
          </Paper>

          <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3, flex: 1 }}>
            <Stack spacing={1.5}>
              <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                Upload Holdings Excel
              </Typography>

              <Stack direction={{ xs: "column", md: "row" }} spacing={1.2}>
                <TextField
                  size="small"
                  type="date"
                  label="Snapshot date"
                  InputLabelProps={{ shrink: true }}
                  value={holdingsDate}
                  onChange={(e) => setHoldingsDate(e.target.value)}
                  sx={{ maxWidth: 220 }}
                />
                <Button variant="outlined" component="label">
                  {holdingsFile ? holdingsFile.name : "Select Holdings Excel"}
                  <input
                    hidden
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={(e) => setHoldingsFile(e.target.files?.[0] || null)}
                  />
                </Button>
                <Button
                  variant="contained"
                  onClick={onUploadHoldings}
                  disabled={uploadingHoldings}
                >
                  {uploadingHoldings ? "Uploading..." : "Upload Holdings"}
                </Button>
              </Stack>

              {holdingsUploadError ? <Alert severity="error">{holdingsUploadError}</Alert> : null}
              {holdingsUploadSummary ? (
                <Alert severity="success">
                  Snapshot {holdingsUploadSummary.snapshot_date}:{" "}
                  {holdingsUploadSummary.inserted_rows} inserted across{" "}
                  {holdingsUploadSummary.etf_count} ETFs ({holdingsUploadSummary.skipped_rows} skipped)
                </Alert>
              ) : null}

              <Divider />

              <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                Recent Holdings Imports
              </Typography>

              {loadingHoldingsImports ? (
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  Loading holdings import history...
                </Typography>
              ) : holdingsImports.length ? (
                <Stack spacing={0.6}>
                  {holdingsImports.map((r) => (
                    <Stack
                      key={r.import_batch_id}
                      direction={{ xs: "column", md: "row" }}
                      justifyContent="space-between"
                      sx={{ p: 1, borderRadius: 1.5, background: "rgba(148,163,184,0.08)" }}
                    >
                      <Typography variant="body2" sx={{ fontWeight: 700 }}>
                        {r.snapshot_date} - {r.source_file_name || "file"}
                      </Typography>
                      <Typography variant="body2" sx={{ opacity: 0.8 }}>
                        P:{r.processed_rows} I:{r.inserted_rows} S:{r.skipped_rows}
                      </Typography>
                    </Stack>
                  ))}
                </Stack>
              ) : (
                <Typography variant="body2" sx={{ opacity: 0.72 }}>
                  No holdings imports yet.
                </Typography>
              )}
            </Stack>
          </Paper>
        </Stack>
      ) : null}

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack spacing={1.5}>
          <Stack direction={{ xs: "column", md: "row" }} spacing={1} alignItems={{ md: "center" }}>
            <TextField
              size="small"
              type="date"
              label="Date"
              InputLabelProps={{ shrink: true }}
              value={filters.date}
              onChange={(e) => {
                setFilters((p) => ({ ...p, date: e.target.value }));
                setDetailDate(e.target.value);
              }}
              sx={{ maxWidth: 210 }}
            />
            <TextField
              size="small"
              label="Search symbol / ETF / AMC"
              value={filters.q}
              onChange={(e) => setFilters((p) => ({ ...p, q: e.target.value }))}
              sx={{ minWidth: { xs: "100%", md: 320 } }}
            />
            <Button variant="outlined" onClick={() => setRefreshNo((x) => x + 1)}>
              Refresh
            </Button>
            <Chip label={`${rows.length} symbols`} />
          </Stack>

          {listMeta.effective_date ? (
            <Typography variant="caption" sx={{ opacity: 0.72 }}>
              Showing date: {listMeta.effective_date}
              {listMeta.used_fallback_date ? " (today not found, latest used)" : ""}
            </Typography>
          ) : null}
          {listError ? <Alert severity="error">{listError}</Alert> : null}
        </Stack>

        <Box sx={{ mt: 1.5 }}>
          <DataGrid
            rows={rows}
            columns={columns}
            loading={loadingList}
            autoHeight
            disableVirtualization
            disableRowSelectionOnClick
            pageSizeOptions={[25, 50, 100]}
            initialState={{
              pagination: { paginationModel: { page: 0, pageSize: 25 } },
            }}
            onRowClick={(params) => {
              setSelectedSymbol(params.row?.symbol || "");
              setDetailDate(filters.date || "");
            }}
          />
        </Box>
      </Paper>

      <Paper elevation={0} sx={{ p: 2.5, borderRadius: 3 }}>
        <Stack spacing={1.5}>
          <Stack
            direction={{ xs: "column", md: "row" }}
            alignItems={{ md: "center" }}
            justifyContent="space-between"
            spacing={1}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
              Symbol Details {selectedSymbol ? `- ${selectedSymbol}` : ""}
            </Typography>
            <TextField
              select
              size="small"
              label="Date"
              value={detailDate}
              onChange={(e) => setDetailDate(e.target.value)}
              sx={{ minWidth: 240 }}
              disabled={!selectedSymbol}
            >
              <MenuItem value="">Auto (today if available)</MenuItem>
              {availableDates.map((d) => (
                <MenuItem key={d} value={d}>
                  {d}
                </MenuItem>
              ))}
            </TextField>
          </Stack>

          {detailError ? <Alert severity="error">{detailError}</Alert> : null}
          {!selectedSymbol ? (
            <Typography variant="body2" sx={{ opacity: 0.72 }}>
              Select a symbol from the table to view details.
            </Typography>
          ) : loadingDetail ? (
            <Typography variant="body2" sx={{ opacity: 0.72 }}>
              Loading details...
            </Typography>
          ) : detailData ? (
            <Stack spacing={1.2}>
              <Box
                sx={{
                  display: "grid",
                  gap: 1.2,
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "repeat(2, minmax(0, 1fr))",
                    md: "repeat(3, minmax(0, 1fr))",
                  },
                }}
              >
                <Stack
                  sx={{
                    p: 1.2,
                    borderRadius: 1.5,
                    background: "rgba(148,163,184,0.08)",
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    LTP
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {fmtNum(detailData?.ltp)}
                  </Typography>
                </Stack>

                <Stack
                  sx={{
                    p: 1.2,
                    borderRadius: 1.5,
                    background: "rgba(148,163,184,0.08)",
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    NAV
                  </Typography>
                  <Typography variant="h6" sx={{ fontWeight: 800 }}>
                    {fmtNum(detailData?.nav)}
                  </Typography>
                </Stack>

                <Stack
                  sx={{
                    p: 1.2,
                    borderRadius: 1.5,
                    background: "rgba(148,163,184,0.08)",
                  }}
                >
                  <Typography variant="caption" sx={{ opacity: 0.75 }}>
                    Premium / Discount
                  </Typography>
                  {premiumInfo ? (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="h6" sx={{ fontWeight: 800 }}>
                        {fmtSignedPct(premiumInfo.premiumPct)}
                      </Typography>
                      <Chip size="small" color={premiumInfo.tone} label={premiumInfo.label} />
                    </Stack>
                  ) : (
                    <Typography variant="body2" sx={{ opacity: 0.72 }}>
                      Requires both LTP and NAV
                    </Typography>
                  )}
                </Stack>
              </Box>

              {premiumInfo ? (
                <Box
                  sx={{
                    p: 1.2,
                    borderRadius: 1.5,
                    background: "rgba(148,163,184,0.08)",
                    height: 280,
                  }}
                >
                  <Stack spacing={0.8} sx={{ height: "100%" }}>
                    <Stack
                      direction={{ xs: "column", sm: "row" }}
                      spacing={1}
                      justifyContent="space-between"
                      alignItems={{ sm: "center" }}
                    >
                      <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                        LTP vs NAV Trend
                      </Typography>
                      <TextField
                        select
                        size="small"
                        label="Range"
                        value={historyRange}
                        onChange={(e) => setHistoryRange(e.target.value)}
                        sx={{ width: { xs: "100%", sm: 120 } }}
                      >
                        {HISTORY_RANGE_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Stack>

                    {loadingHistory ? (
                      <Typography variant="body2" sx={{ opacity: 0.72 }}>
                        Loading comparative trend...
                      </Typography>
                    ) : historyError ? (
                      <Alert severity="error">{historyError}</Alert>
                    ) : filteredHistoryRows.length ? (
                      <Box sx={{ flex: 1, minHeight: 0 }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <LineChart
                            data={filteredHistoryRows}
                            margin={{ top: 8, right: 14, left: 4, bottom: 2 }}
                          >
                            <CartesianGrid strokeDasharray="3 3" vertical={false} />
                            <XAxis dataKey="trade_date" tickFormatter={(v) => String(v).slice(5)} minTickGap={24} />
                            <YAxis yAxisId="value" />
                            <YAxis
                              yAxisId="pct"
                              orientation="right"
                              tickFormatter={fmtPctTick}
                              width={56}
                            />
                            <RTooltip
                              formatter={(value, name) => {
                                if (name === "premium_discount_pct") {
                                  return [fmtSignedPct(value), "Premium/Discount %"];
                                }
                                return [fmtNum(value), name === "ltp" ? "LTP" : "NAV"];
                              }}
                              labelFormatter={(label) => `Date: ${label}`}
                            />
                            <Legend
                              formatter={(value) =>
                                value === "ltp"
                                  ? "LTP"
                                  : value === "nav"
                                  ? "NAV"
                                  : value === "premium_discount_pct"
                                  ? "Premium/Discount %"
                                  : value
                              }
                            />
                            <Line
                              type="monotone"
                              dataKey="ltp"
                              name="ltp"
                              yAxisId="value"
                              stroke="#ef4444"
                              strokeWidth={2.4}
                              dot={false}
                              connectNulls
                            />
                            <Line
                              type="monotone"
                              dataKey="nav"
                              name="nav"
                              yAxisId="value"
                              stroke="#3b82f6"
                              strokeWidth={2.4}
                              dot={false}
                              connectNulls
                            />
                            <Line
                              type="monotone"
                              dataKey="premium_discount_pct"
                              name="premium_discount_pct"
                              yAxisId="pct"
                              stroke="#f59e0b"
                              strokeWidth={2}
                              strokeDasharray="6 4"
                              dot={false}
                              connectNulls
                            />
                          </LineChart>
                        </ResponsiveContainer>
                      </Box>
                    ) : (
                      <Typography variant="body2" sx={{ opacity: 0.72 }}>
                        No trend data available for selected range.
                      </Typography>
                    )}
                  </Stack>
                </Box>
              ) : (
                <Alert severity="info">Premium/Discount requires both LTP and NAV.</Alert>
              )}

              <Typography variant="caption" sx={{ opacity: 0.72 }}>
                Guide: -0.5% to 0.5% Fair, 0.5% to 1.5% Mild, 1.5% to 3% Moderate, above 3% High
              </Typography>

              <Box
                sx={{
                  display: "grid",
                  gap: 1.2,
                  gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" },
                }}
              >
                {detailFields.map((f) => (
                  <Stack
                    key={f.key}
                    direction="row"
                    justifyContent="space-between"
                    sx={{
                      p: 1,
                      borderRadius: 1.5,
                      background: "rgba(148,163,184,0.08)",
                      alignItems: "center",
                    }}
                  >
                    <Typography variant="body2" sx={{ opacity: 0.78 }}>
                      {f.label}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 700, ml: 2, textAlign: "right" }}>
                      {renderDetailValue(f.key, detailData[f.key])}
                    </Typography>
                  </Stack>
                ))}
              </Box>
            </Stack>
          ) : (
            <Typography variant="body2" sx={{ opacity: 0.72 }}>
              No details available for the selected symbol/date.
            </Typography>
          )}
        </Stack>
      </Paper>
    </Stack>
  );
}
